<template>
  <div class="participant-detail">
    <router-link :to="{ name: 'ResultsDashboard' }" class="back-link">&larr; Kembali ke Daftar Peserta</router-link>

    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner"></div>
      <p>Memuat data peserta...</p>
    </div>

    <div v-else-if="participant">
      <!-- 1. HEADER AKSI -->
      <header class="page-header card">
        <div class="title-section">
          <h1>Laporan: {{ participant.name }}</h1>
          <div class="meta-badges">
            <span class="badge-pkg">{{ participant.package_name }}</span>
            <span v-if="report && report.result_type === 'manual'" class="badge-vip">JALUR VIP</span>
          </div>
        </div>
        <div class="header-actions">
          <button @click="generateReport" :disabled="isProcessing" class="btn-action primary">
             {{ isProcessing ? '...' : '1. Hitung Skor Otomatis' }}
          </button>
          <button @click="saveFullReport" :disabled="!report || isProcessing" class="btn-action success">
             2. Simpan Draft Narasi
          </button>
          <button @click="generateProfessionalPdf" :disabled="!report" class="btn-action danger">
             3. Cetak PDF Laporan
          </button>
        </div>
      </header>

      <!-- 2. INFORMASI IDENTITAS -->
      <div class="details-grid card">
        <div class="detail-item"><strong>Nomor Registrasi</strong><span>{{ participant.test_number }}</span></div>
        <div class="detail-item"><strong>Tanggal Lahir</strong><span>{{ formatDate(participant.birth_date, true) }}</span></div>
        <div class="detail-item"><strong>Pekerjaan</strong><span>{{ participant.job || '-' }}</span></div>
        <div class="detail-item"><strong>Tanggal Tes</strong><span>{{ formatDate(participant.created_at) }}</span></div>
        <div class="detail-item full"><strong>Posisi Dituju</strong><input v-model="reportFields.target_position" placeholder="Contoh: Tenaga Layanan - Psikolog Klinis"></div>
      </div>


    <!-- Sisipkan di bawah 'details-grid card' dan di ATAS 'report-editor' -->

    <div class="raw-data-section card">
      <div class="section-title">
        <i class="fas fa-microscope"></i>
        <h2>Verifikasi Data Mentah & Skor</h2>
      </div>
      <p class="hint">Periksa jawaban mentah peserta dan pastikan skor sudah sesuai sebelum men-generate laporan narasi.</p>
      
      <div class="table-container">
        <table class="verification-table">
          <thead>
            <tr>
              <th>Nama Tes</th>
              <th>Status</th>
              <th>Skor Saat Ini</th>
              <th class="text-center">Data Mentah</th>
              <th class="text-center">Aksi</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="session in participant.sessions" :key="session.id">
              <td><strong>{{ session.name }}</strong></td>
              <td>
                <span class="status-badge" :class="session.status">
                  {{ formatStatus(session.status) }}
                </span>
              </td>
              <td>
                <div class="score-edit-wrapper">
                  <input 
                    type="number" 
                    v-model.number="session.score" 
                    class="score-input"
                    @change="markAsChanged(session.id)"
                  >
                  <button 
                    v-if="changedScores.includes(session.id)" 
                    @click="updateManualScore(session)"
                    class="btn-save-mini"
                  >
                    Update
                  </button>
                </div>
              </td>
              <td class="text-center">
                <!-- Tombol Download Excel Per Tes -->
                 <!-- Gunakan session.id, karena di schema TestStatus, 'id' adalah ID Master Tes -->
              <button
                v-if="session.status === 'completed'"
                @click="downloadAnswersPerTest(session.id, session.name)"
                :disabled="isDownloading === session.id"
                class="btn-download-excel"
              >
                <i class="fas fa-file-excel"></i> Excel
              </button>
              <button
                v-if="session.status === 'completed'"
                @click="goToEssayCorrection(session.id)"
                class="btn-koreksi-essay"
                style="margin-left: 5px;"
              >
                <i class="fas fa-edit"></i> Koreksi Essay
              </button>
                <span v-else class="text-muted">-</span>
              </td>
              <td class="text-center">
                <button class="btn-text-only">Lihat Detail</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
      <!-- 3. EDITOR NARASI (Hanya muncul jika report sudah ada) -->
      <div v-if="report" class="report-editor">
        
        <!-- BAGIAN A: IQ -->
        <section class="editor-section card">
          <div class="section-title">
            <i class="fas fa-brain"></i>
            <h2>A. PROFIL POTENSI INDIVIDU (IQ)</h2>
          </div>
          <div class="iq-config-row">
             <div class="input-group">
               <label>Skor IQ</label>
               <input type="number" v-model.number="reportFields.iq_score">
             </div>
             <div class="input-group">
               <label>Kategori IQ</label>
               <input v-model="reportFields.iq_category" placeholder="e.g. High Average">
             </div>
          </div>
          <label class="label-block">Analisis Naratif Potensi IQ:</label>
          <textarea v-model="reportFields.iq_narrative" rows="5"></textarea>
        </section>

        <!-- BAGIAN B: PSIKOGRAM PREVIEW -->
        <section class="editor-section card">
          <div class="section-title">
            <i class="fas fa-table"></i>
            <h2>B. ASPEK PSIKOLOGIS (PSIKOGRAM)</h2>
          </div>
          <div class="table-container">
            <table class="preview-table">
              <thead>
                <tr><th>Aspek</th><th>Sub-Aspek</th><th>Kategori</th></tr>
              </thead>
              <tbody>
                <template v-for="aspect in parsedSummary.aspects" :key="aspect.name">
                  <tr v-for="(sub, idx) in aspect.sub_aspects" :key="sub.name">
                    <td v-if="idx === 0" :rowspan="aspect.sub_aspects.length" class="bg-light"><strong>{{ aspect.name }}</strong></td>
                    <td>{{ sub.name }}</td>
                    <td class="text-center">
                      <select v-model="sub.category" class="select-cat">
                        <option value="1">1</option><option value="2">2</option>
                        <option value="3">3</option><option value="4">4</option>
                        <option value="5">5</option>
                        <option value="R">R</option><option value="K">K</option>
                        <option value="C">C</option><option value="B">B</option>
                        <option value="T">T</option>
                      </select>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </section>

        <!-- BAGIAN C: PROFIL DISC -->
        <section v-if="reportFields.disc_data" class="editor-section card">
          <div class="section-title">
            <i class="fas fa-chart-bar"></i>
            <h2>C. PROFIL KEPRIBADIAN (DISC)</h2>
          </div>
          <div class="disc-tables-grid">
            <div class="disc-table">
              <h4 class="text-center">Graph I (Public)</h4>
              <table class="preview-table text-center">
                <thead>
                  <tr><th>D</th><th>I</th><th>S</th><th>C</th><th>*</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{{ reportFields.disc_data.graph_1_most['D'] }}</td>
                    <td>{{ reportFields.disc_data.graph_1_most['I'] }}</td>
                    <td>{{ reportFields.disc_data.graph_1_most['S'] }}</td>
                    <td>{{ reportFields.disc_data.graph_1_most['C'] }}</td>
                    <td>{{ reportFields.disc_data.graph_1_most['*'] }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="disc-table">
              <h4 class="text-center">Graph II (Private)</h4>
              <table class="preview-table text-center">
                <thead>
                  <tr><th>D</th><th>I</th><th>S</th><th>C</th><th>*</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{{ reportFields.disc_data.graph_2_least['D'] }}</td>
                    <td>{{ reportFields.disc_data.graph_2_least['I'] }}</td>
                    <td>{{ reportFields.disc_data.graph_2_least['S'] }}</td>
                    <td>{{ reportFields.disc_data.graph_2_least['C'] }}</td>
                    <td>{{ reportFields.disc_data.graph_2_least['*'] }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="disc-table">
              <h4 class="text-center">Graph III (Mirror)</h4>
              <table class="preview-table text-center">
                <thead>
                  <tr><th>D</th><th>I</th><th>S</th><th>C</th><th>*</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{{ reportFields.disc_data.graph_3_change['D'] }}</td>
                    <td>{{ reportFields.disc_data.graph_3_change['I'] }}</td>
                    <td>{{ reportFields.disc_data.graph_3_change['S'] }}</td>
                    <td>{{ reportFields.disc_data.graph_3_change['C'] }}</td>
                    <td>{{ reportFields.disc_data.graph_3_change['*'] }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <!-- BAGIAN D: KEPRIBADIAN -->
        <section class="editor-section card">
          <div class="section-title">
            <i class="fas fa-user-tie"></i>
            <h2>D. PREFERENSI KEPRIBADIAN</h2>
          </div>
          <div class="form-group mb-3">
            <label>Tipe Kepribadian (e.g. ISFJ / ENFP)</label>
            <input v-model="reportFields.personality_type" class="input-full">
          </div>
          <label class="label-block">Narasi Kepribadian:</label>
          <textarea v-model="reportFields.personality_narrative" rows="6"></textarea>
        </section>

        <!-- BAGIAN E: KESIMPULAN -->
        <section class="editor-section card">
          <div class="section-title">
            <i class="fas fa-check-circle"></i>
            <h2>E. KESIMPULAN & REKOMENDASI</h2>
          </div>
          <div class="grid-2">
            <div class="form-group">
              <label class="label-block">Area Kekuatan:</label>
              <textarea v-model="reportFields.strengths_list" rows="5"></textarea>
            </div>
            <div class="form-group">
              <label class="label-block">Area Pengembangan:</label>
              <textarea v-model="reportFields.weaknesses_list" rows="5"></textarea>
            </div>
          </div>

          <div class="recommendation-grid mt-4">
             <div class="form-group">
                <label>Presentase Kesesuaian (%)</label>
                <input v-model="reportFields.overall_score" placeholder="e.g. 92%">
             </div>
             <div class="form-group">
                <label>Hasil Rekomendasi</label>
                <select v-model="reportFields.conclusion" class="input-full">
                  <option value="Disarankan">Disarankan</option>
                  <option value="Dipertimbangkan">Dipertimbangkan</option>
                  <option value="Tidak Disarankan">Tidak Disarankan</option>
                </select>
             </div>
          </div>

          <div class="form-group mt-3">
            <label class="label-block">Saran Pengembangan:</label>
            <textarea v-model="reportFields.development_suggestions" rows="4"></textarea>
          </div>
        </section>

      </div>
    </div>
    <p v-if="error" class="error-text">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import apiClient from '@/services/api.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import qr from 'qrcode';
const isDownloading = ref(null); 
// Assets
import watermarkImage from '@/assets/watermark.png';
import logoImage from '@/assets/logo1.png';

const route = useRoute();
const router = useRouter();
const participantId = route.params.id;

// States
const participant = ref(null);
const report = ref(null);
const adminProfile = ref(null);
const isLoading = ref(true);
const isProcessing = ref(false);

// Form Fields
const reportFields = ref({
  target_position: '',
  iq_score: 100,
  iq_category: '',
  iq_narrative: '',
  personality_type: '',
  personality_narrative: '',
  strengths_list: '',
  weaknesses_list: '',
  overall_score: '',
  conclusion: 'Dipertimbangkan',
  development_suggestions: '',
  aspects_data: [], // Untuk menyimpan edit kategori R-T
  disc_data: null
});
// --- Tambahkan State untuk melacak perubahan skor manual ---
const changedScores = ref([]);

const goToEssayCorrection = (testId) => {
  router.push(`/admin/participant/${route.params.id}/session/${testId}/essay`);
};

// --- Fungsi untuk memformat status teks (UNTUK MEMPERBAIKI ERROR) ---
const formatStatus = (status) => {
  if (status === 'not_started') return 'Belum Dikerjakan';
  if (status === 'in_progress') return 'Sedang Dikerjakan';
  if (status === 'completed') return 'Selesai';
  return status;
};

// --- Fungsi untuk menandai jika ada input skor yang diubah ---
const markAsChanged = (sessionId) => {
  if (!changedScores.value.includes(sessionId)) {
    changedScores.value.push(sessionId);
  }
};

// --- Fungsi untuk menyimpan skor hasil koreksi manual ke Database ---
const updateManualScore = async (session) => {
  try {
    // Memanggil API yang sudah kita buat di backend (Fase 20)
    await apiClient.put(`/admin/sessions/${session.id}/manual-score`, {
      score: session.score
    });
    
    // Hapus ID dari daftar "changed" setelah berhasil simpan
    changedScores.value = changedScores.value.filter(id => id !== session.id);
    
    alert(`Skor untuk ${session.name} berhasil diperbarui.`);
  } catch (err) {
    console.error(err);
    alert("Gagal memperbarui skor. Pastikan backend sudah didukung manual-score.");
  }
};

// --- Fungsi untuk Mendownload Jawaban Per Tes (Excel) ---
const downloadAnswersPerTest = async (testId, testName) => {
  if (!testId) {
    alert("ID Tes tidak valid");
    return;
  }
  isDownloading.value = testId;
  try {
    const response = await apiClient.get(
      `/admin/participants/${participantId}/tests/${testId}/download`,
      { responseType: 'blob' }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Jawaban_${testName}_${participant.value.name}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error(err);
    alert("Gagal download excel");
  } finally {
    isDownloading.value = null;
  }
};


const fetchData = async () => {
  isLoading.value = true;
  try {
    const [pRes, rRes, aRes] = await Promise.all([
      apiClient.get(`/admin/participants/${participantId}`),
      apiClient.get(`/admin/participants/${participantId}/report`).catch(() => ({ data: null })),
      apiClient.get('/admin/me').catch(() => ({ data: null }))
    ]);
    participant.value = pRes.data;
    adminProfile.value = aRes.data;
    if (rRes.data) {
      report.value = rRes.data;
      fillFields(rRes.data);
    }
  } finally {
    isLoading.value = false;
  }
};

const fillFields = (data) => {
  const summary = data.interpretation_summary ? JSON.parse(data.interpretation_summary) : {};
  reportFields.value = {
    ...data,
    aspects_data: summary.aspects || [],
    disc_data: summary.disc_data || null
  };
};

const parsedSummary = computed(() => {
  return { aspects: reportFields.value.aspects_data };
});

const generateReport = async () => {
  if (!confirm("Hitung ulang skor akan menimpa narasi yang sedang diedit. Lanjutkan?")) return;
  isProcessing.value = true;
  try {
    const res = await apiClient.post(`/admin/participants/${participantId}/generate-report`);
    report.value = res.data;
    fillFields(res.data);
  } finally { isProcessing.value = false; }
};

const saveFullReport = async () => {
  isProcessing.value = true;
  try {
    // Pack kembali aspek data ke string JSON sebelum kirim
    const payload = {
      ...reportFields.value,
      interpretation_summary: JSON.stringify({ 
        aspects: reportFields.value.aspects_data,
        disc_data: reportFields.value.disc_data 
      })
    };
    const res = await apiClient.put(`/admin/results/${report.value.id}/full-update`, payload);
    report.value = res.data;
    alert("Draft Laporan Berhasil Disimpan!");
  } finally { isProcessing.value = false; }
};

// Helper untuk load gambar dari URL menjadi HTMLImageElement agar aman di jsPDF
const loadImageElement = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Gagal memuat gambar: ${src}`));
    img.src = src;
  });
};

const generateProfessionalPdf = async () => {
  try {
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: [210, 297] });
    const p = participant.value;
    const rf = reportFields.value;
    const a = adminProfile.value || {};

    // Load aset gambar terlebih dahulu menjadi Image element
    const logoImgElement = await loadImageElement(logoImage);
    const watermarkImgElement = await loadImageElement(watermarkImage);

    // --- HALAMAN 1: COVER & IQ ---
    // Background Watermark (Opacity rendah)
    const addWatermark = () => {
      doc.saveGraphicsState();
      doc.setGState(new doc.GState({ opacity: 0.1 }));
      doc.addImage(watermarkImgElement, 'PNG', 55, 100, 100, 100);
      doc.restoreGraphicsState();
    };

  addWatermark();
  doc.addImage(logoImgElement, 'PNG', 10, 10, 25, 25);
  doc.setFontSize(10).setFont('helvetica', 'bold').text("PT. PRERANA ETOS INSPIRASI", 105, 15, { align: 'center' });
  doc.setFontSize(14).text("LAPORAN ASESMEN PSIKOLOGIS", 105, 35, { align: 'center' });
  
  doc.setFontSize(11).setFont('helvetica', 'normal');
  let y = 50;
  const row = (label, val) => { doc.text(label, 20, y); doc.text(`: ${val}`, 65, y); y += 8; };
  row("Nama", p.name);
  row("Nomor Registrasi", p.test_number);
  row("Tanggal Lahir", formatDate(p.birth_date, true));
  row("Posisi Dituju", rf.target_position);

  y += 10;
  doc.setFont('helvetica', 'bold').text("A. PROFIL POTENSI INDIVIDU", 20, y);
  doc.line(20, y+2, 190, y+2);
  
  y += 12;
  doc.setFillColor(245, 245, 245).rect(20, y, 170, 10, 'F');
  doc.text(`KAPASITAS IQ: ${rf.iq_score} / ${rf.iq_category}`, 105, y+7, { align: 'center' });
  
  y += 20;
  doc.setFont('helvetica', 'normal');
  const iqSplit = doc.splitTextToSize(rf.iq_narrative, 170);
  doc.text(iqSplit, 20, y);

  // --- HALAMAN 2: PSIKOGRAM ---
  doc.addPage();
  addWatermark();
  doc.setFont('helvetica', 'bold').text("B. PSIKOGRAM (ASPEK PSIKOLOGIS)", 20, 20);
  
  const tableBody = [];
  rf.aspects_data.forEach(asp => {
    tableBody.push([{ content: asp.name, colSpan: 6, styles: { fillColor: [240, 240, 240], fontStyle: 'bold' } }]);
    asp.sub_aspects.forEach(sub => {
      tableBody.push([
        sub.name, 
        sub.category === 'R' ? 'X' : '', 
        sub.category === 'K' ? 'X' : '', 
        sub.category === 'C' ? 'X' : '', 
        sub.category === 'B' ? 'X' : '', 
        sub.category === 'T' ? 'X' : ''
      ]);
    });
  });

  autoTable(doc, {
    startY: 25,
    head: [['Aspek', 'R', 'K', 'C', 'B', 'T']],
    body: tableBody,
    theme: 'grid',
    headStyles: { halign: 'center', fillColor: [52, 73, 94] },
    columnStyles: { 0: { cellWidth: 70 }, 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' } }
  });

  // --- HALAMAN DISC ---
  if (rf.disc_data) {
    doc.addPage();
    addWatermark();
    let discY = 20;
    doc.setFont('helvetica', 'bold').text("C. PROFIL KEPRIBADIAN (DISC)", 20, discY);
    
    autoTable(doc, {
      startY: discY + 10,
      head: [['', 'D', 'I', 'S', 'C', '*']],
      body: [
        ['Graph I (Public)', rf.disc_data.graph_1_most['D'], rf.disc_data.graph_1_most['I'], rf.disc_data.graph_1_most['S'], rf.disc_data.graph_1_most['C'], rf.disc_data.graph_1_most['*']],
        ['Graph II (Private)', rf.disc_data.graph_2_least['D'], rf.disc_data.graph_2_least['I'], rf.disc_data.graph_2_least['S'], rf.disc_data.graph_2_least['C'], rf.disc_data.graph_2_least['*']],
        ['Graph III (Mirror)', rf.disc_data.graph_3_change['D'], rf.disc_data.graph_3_change['I'], rf.disc_data.graph_3_change['S'], rf.disc_data.graph_3_change['C'], rf.disc_data.graph_3_change['*']],
      ],
      theme: 'grid',
      headStyles: { halign: 'center', fillColor: [52, 73, 94] },
      columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' } }
    });
  }

  // --- HALAMAN SELANJUTNYA: KEPRIBADIAN & KESIMPULAN ---
  doc.addPage();
  addWatermark();
  y = 20;
  doc.setFont('helvetica', 'bold').text("D. PREFERENSI KEPRIBADIAN", 20, y);
  doc.setFont('helvetica', 'normal').text(`Tipe: ${rf.personality_type}`, 20, y + 8);
  const persSplit = doc.splitTextToSize(rf.personality_narrative, 170);
  doc.text(persSplit, 20, y + 16);

  y = (doc.lastAutoTable ? doc.lastAutoTable.finalY : y) + 10;
  doc.setFont('helvetica', 'bold').text("E. KESIMPULAN & REKOMENDASI", 20, y);
  doc.line(20, y+2, 190, y+2);

  y += 12;
  doc.text("Area Kekuatan:", 20, y);
  doc.setFont('helvetica', 'normal').text(doc.splitTextToSize(rf.strengths_list, 170), 20, y + 7);
  
  y += 40;
  doc.setFont('helvetica', 'bold').text("Rekomendasi:", 20, y);
  doc.setFont('helvetica', 'normal').text(`${rf.conclusion} (${rf.overall_score})`, 60, y);

  // --- TANDA TANGAN & QR ---
  y = 250;
  const qrUrl = `https://psikotestonline.id/verify?id=${p.test_number}`;
  const qrDataUri = await qr.toDataURL(qrUrl);
  doc.addImage(qrDataUri, 'PNG', 20, y, 25, 25);
  doc.setFontSize(8).text("Verifikasi Laporan", 20, y + 30);

  doc.setFontSize(11).text(`${a.city || 'Jakarta'}, ${formatDate(new Date(), true)}`, 140, y);
  doc.setFont('helvetica', 'bold').text("Psikolog Pemeriksa,", 140, y + 7);
  
  if (a.signature_image_url) {
     try {
       const sigUrl = `https://psikotestonline.id${a.signature_image_url}`;
       const sigImg = await loadImageElement(sigUrl);
       const isJpeg = sigUrl.toLowerCase().match(/\.jpe?g$/);
       doc.addImage(sigImg, isJpeg ? 'JPEG' : 'PNG', 140, y + 10, 30, 15);
     } catch (e) {
       console.warn("Gagal memuat tanda tangan:", e);
     }
  }
  doc.text(a.full_name || "Nama Psikolog", 140, y + 35);
  doc.setFontSize(9).text(`SIPP: ${a.sipp_number || '-'}`, 140, y + 40);

  doc.save(`Laporan_${p.name}.pdf`);
  } catch (error) {
    console.error("PDF Generation Error:", error);
    alert(`Gagal mencetak PDF. Silakan periksa console browser. Detail: ${error.message}`);
  }
};

const formatDate = (dateString, onlyDate = false) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  return d.toLocaleDateString('id-ID', { 
    day: 'numeric', month: 'long', year: 'numeric',
    ...(onlyDate ? {} : { hour: '2-digit', minute: '2-digit' })
  });
};

onMounted(fetchData);
</script>

<style scoped>
.participant-detail { max-width: 1200px; margin: auto; padding: 20px; font-family: 'Inter', sans-serif; }
.card { background: white; border-radius: 12px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); padding: 25px; margin-bottom: 25px; }
.page-header { display: flex; justify-content: space-between; align-items: center; border-left: 5px solid #3498db; flex-wrap: wrap; gap: 15px; }
.header-actions { display: flex; gap: 10px; flex-wrap: wrap; }
.badge-pkg { background: #e1f0fa; color: #3498db; padding: 4px 12px; border-radius: 20px; font-weight: bold; font-size: 0.8em; }
.badge-vip { background: #f3e5f5; color: #9c27b0; padding: 4px 12px; border-radius: 20px; font-weight: bold; font-size: 0.8em; margin-left: 10px; }
.details-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
.detail-item.full { grid-column: span 4; }
.detail-item input { width: 100%; border: 1px solid #ddd; padding: 8px; border-radius: 6px; }
.section-title { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; color: #2c3e50; border-bottom: 2px solid #eee; padding-bottom: 10px; }
.iq-config-row { display: flex; gap: 30px; margin-bottom: 20px; }
textarea { width: 100%; border: 1px solid #ddd; border-radius: 8px; padding: 15px; font-size: 1em; line-height: 1.6; transition: 0.3s; }
textarea:focus { border-color: #3498db; outline: none; box-shadow: 0 0 8px rgba(52, 152, 219, 0.2); }
.preview-table { width: 100%; border-collapse: collapse; }
.preview-table th, .preview-table td { border: 1px solid #eee; padding: 12px; text-align: left; }
.select-cat { padding: 4px; font-weight: bold; border-radius: 4px; border: 1px solid #ccc; }
.btn-action { padding: 12px 24px; border-radius: 8px; border: none; font-weight: bold; cursor: pointer; color: white; transition: 0.2s; }
.btn-action:hover { opacity: 0.9; transform: translateY(-2px); }
.primary { background: #3498db; }
.success { background: #27ae60; }
.danger { background: #e74c3c; }

.verification-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}
.verification-table th {
  background: #f8f9fa;
  color: #333;
  text-transform: uppercase;
  font-size: 0.85em;
  letter-spacing: 0.5px;
}
.score-edit-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}
.score-input {
  width: 70px;
  padding: 5px;
  border: 1px solid #ddd;
  border-radius: 4px;
  text-align: center;
  font-weight: bold;
}
.btn-save-mini {
  padding: 4px 8px;
  background: #27ae60;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.75em;
  cursor: pointer;
}
.btn-download-excel {
  background: #1d6f42; /* Warna khas Excel */
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85em;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.btn-download-excel:hover {
  background: #145a32;
}

.btn-koreksi-essay {
  padding: 4px 10px;
  background: #e67e22; /* Warna khas edit/warning */
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85em;
  font-weight: 500;
  transition: background-color 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.btn-koreksi-essay:hover {
  background: #d35400;
}

.btn-text-only { color: #999; }
.text-muted { color: #999; }
.hint {
  font-size: 0.9em;
  color: #666;
  margin-bottom: 15px;
  font-style: italic;
}
.disc-tables-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 15px;
}
.disc-table h4 {
  margin-bottom: 10px;
  color: #2c3e50;
}
.text-center {
  text-align: center;
}
</style>